<script setup>
// SiteUp: modo de report da IA. Um formulário só para os pontos de entrada
// (sugestão, copiloto, resposta automática e balão da Central na conversa): o que a IA disse, o que
// era pra ter dito, os motivos e o porquê. Vai pra fila de revisão da Central.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [Number, String], required: true },
  // suggestion | copilot | autonomous | ai_center
  source: { type: String, default: 'suggestion' },
  aiText: { type: String, default: '' },
  customerText: { type: String, default: '' },
  messageId: { type: [Number, String], default: null },
  // Balão da Central (source ai_center, D50): o item reportado.
  aiCenterItemId: { type: [Number, String], default: null },
});

const emit = defineEmits(['close', 'sent']);

const { t } = useI18n();

const CATEGORIES = [
  'inventou_dado',
  'info_errada',
  'tom',
  'devia_handoff',
  'pulou_pergunta',
  'fora_do_escopo',
];

const dialogRef = ref(null);
const expected = ref('');
const reason = ref('');
const picked = ref([]);
const pauseAi = ref(true);
const sending = ref(false);

const isAutonomous = computed(() => props.source === 'autonomous');
const canSend = computed(
  () => !sending.value && (picked.value.length > 0 || reason.value.trim())
);

const reset = () => {
  expected.value = props.aiText || '';
  reason.value = '';
  picked.value = [];
  pauseAi.value = true;
  sending.value = false;
};

const sync = value => {
  if (value) {
    reset();
    dialogRef.value?.open();
  } else {
    dialogRef.value?.close();
  }
};

// Os pais montam o modal só quando abrem (v-if): abre ao montar.
onMounted(() => sync(props.show));
watch(() => props.show, sync, { flush: 'post' });

const toggle = key => {
  picked.value = picked.value.includes(key)
    ? picked.value.filter(item => item !== key)
    : [...picked.value, key];
};

const submit = async () => {
  if (!canSend.value) return;
  sending.value = true;
  const edited = expected.value.trim();
  try {
    await ConversationAiAgentAPI.reportFeedback(props.conversationId, {
      source: props.source,
      content: props.aiText,
      message_id: props.messageId,
      expected_content: edited && edited !== props.aiText.trim() ? edited : '',
      categories: picked.value,
      reason: reason.value.trim(),
      ...(props.aiCenterItemId
        ? { ai_action_item_id: props.aiCenterItemId }
        : {}),
    });
  } catch (error) {
    useAlert(t('CONVERSATION.AI_REPORT.ERROR'));
    sending.value = false;
    return;
  }

  // O report já foi gravado: se só a pausa falhar, avisamos e fechamos. Antes o
  // erro deixava o modal aberto e o reenvio criava um report duplicado na fila.
  let message = t('CONVERSATION.AI_REPORT.SENT');
  if (isAutonomous.value && pauseAi.value) {
    try {
      await ConversationAiAgentAPI.update(props.conversationId, {
        paused: true,
      });
    } catch (error) {
      message = t('CONVERSATION.AI_REPORT.SENT_PAUSE_FAILED');
    }
  }
  useAlert(message);
  emit('sent');
  emit('close');
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONVERSATION.AI_REPORT.TITLE')"
    :description="t(`CONVERSATION.AI_REPORT.SOURCE.${source.toUpperCase()}`)"
    :confirm-button-label="t('CONVERSATION.AI_REPORT.SEND')"
    :cancel-button-label="t('CONVERSATION.AI_REPORT.CANCEL')"
    :disable-confirm-button="!canSend"
    :is-loading="sending"
    width="xl"
    @confirm="submit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4" data-testid="ai-report-modal">
      <p
        v-if="isAutonomous"
        class="m-0 flex items-start gap-2 rounded-lg bg-n-amber-3 px-3 py-2 text-sm text-n-amber-12"
      >
        <span class="i-ph-warning-circle mt-0.5 size-4 shrink-0" />
        {{ t('CONVERSATION.AI_REPORT.ALREADY_SENT') }}
      </p>

      <div v-if="customerText" class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-n-slate-11">
          {{ t('CONVERSATION.AI_REPORT.CUSTOMER_SAID') }}
        </span>
        <p
          class="m-0 whitespace-pre-line rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
        >
          {{ customerText }}
        </p>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="text-xs font-semibold text-n-ruby-11">
            {{
              isAutonomous
                ? t('CONVERSATION.AI_REPORT.AI_REPLIED')
                : t('CONVERSATION.AI_REPORT.AI_SUGGESTED')
            }}
          </span>
          <p
            class="m-0 min-h-24 whitespace-pre-line rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-slate-12"
            data-testid="ai-report-ai-text"
          >
            {{ aiText }}
          </p>
        </div>
        <label class="flex min-w-0 flex-col gap-1">
          <span class="text-xs font-semibold text-n-teal-11">
            {{ t('CONVERSATION.AI_REPORT.EXPECTED') }}
            <span class="font-normal text-n-slate-11">
              {{ t('CONVERSATION.AI_REPORT.OPTIONAL') }}
            </span>
          </span>
          <textarea
            v-model="expected"
            rows="4"
            class="min-h-24 w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
            data-testid="ai-report-expected"
          />
        </label>
      </div>

      <fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
        <legend class="mb-2 p-0 text-sm font-semibold text-n-slate-12">
          {{ t('CONVERSATION.AI_REPORT.WHAT_WRONG') }}
        </legend>
        <div class="grid gap-2 sm:grid-cols-2">
          <label
            v-for="key in CATEGORIES"
            :key="key"
            class="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 text-sm text-n-slate-12"
            :class="
              picked.includes(key)
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak bg-n-background hover:border-n-brand'
            "
            :data-testid="`ai-report-category-${key}`"
          >
            <input
              type="checkbox"
              class="size-4"
              :checked="picked.includes(key)"
              @change="toggle(key)"
            />
            {{ t(`CONVERSATION.AI_REPORT.CATEGORY.${key.toUpperCase()}`) }}
          </label>
        </div>
      </fieldset>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('CONVERSATION.AI_REPORT.WHY') }}
        </span>
        <textarea
          v-model="reason"
          rows="3"
          :placeholder="t('CONVERSATION.AI_REPORT.WHY_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          data-testid="ai-report-reason"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('CONVERSATION.AI_REPORT.NEED_ONE') }}
        </span>
      </label>

      <label
        v-if="isAutonomous"
        class="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-n-slate-12"
      >
        <input v-model="pauseAi" type="checkbox" class="size-4" />
        {{ t('CONVERSATION.AI_REPORT.PAUSE_AI') }}
      </label>

      <p class="m-0 text-xs text-n-slate-11">
        {{ t('CONVERSATION.AI_REPORT.FOOTNOTE') }}
      </p>
    </div>
  </Dialog>
</template>
